import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Coins, Share2, Users, ShoppingBag, Store, ChevronLeft, Sparkles, TrendingUp, Shield, Truck, RotateCcw, Headphones, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const features = [
  {
    icon: ShoppingCart,
    label: "קנה",
    emoji: "🛒",
    color: "from-primary/25 to-primary/5",
    border: "border-primary/40",
    glowHsl: "var(--primary)",
    description: "מוצרים ומתנות מספקים ישראליים. משלוח רגיל ₪25 תוך 1-5 ימי עסקים, חינם מעל ₪500 לספק, ותשלום מאובטח.",
    link: "/קטגוריות",
    linkText: "לקטלוג",
  },
  {
    icon: Coins,
    label: "צבור נקודות",
    emoji: "💰",
    color: "from-amber-500/25 to-amber-500/5",
    border: "border-amber-500/40",
    glowHsl: "38 92% 50%",
    description: "על כל קנייה תקבל נקודות. 100 נקודות שוות ₪1 הנחה. גם על כניסה יומית, שיתוף מוצרים ומשימות יומיות.",
    link: "/נקודות",
    linkText: "לנקודות שלי",
  },
  {
    icon: Share2,
    label: "שתף והרוויח",
    emoji: "🔗",
    color: "from-blue-500/25 to-blue-500/5",
    border: "border-blue-500/40",
    glowHsl: "217 91% 60%",
    description: "שתף מוצרים עם חברים. אם הם קונים דרך הלינק שלך, אתה מקבל נקודות בונוס על כל רכישה שלהם.",
    link: "/קטגוריות",
    linkText: "בחר מוצר לשיתוף",
  },
  {
    icon: Users,
    label: "הזמן חברים",
    emoji: "👥",
    color: "from-green-500/25 to-green-500/5",
    border: "border-green-500/40",
    glowHsl: "142 71% 45%",
    description: "הזמן חברים להצטרף ל-NiceGift וקבל נקודות בונוס על כל חבר שנרשם וקונה. ככל שמזמינים יותר, מרוויחים יותר.",
    link: "/הגדרות",
    linkText: "קוד ההפניה שלי",
  },
  {
    icon: ShoppingBag,
    label: "קנה יחד",
    emoji: "🤝",
    color: "from-purple-500/25 to-purple-500/5",
    border: "border-purple-500/40",
    glowHsl: "271 76% 53%",
    description: "צור עגלה משותפת עם חברים ומשפחה. כולם מוסיפים מוצרים, מחלקים את התשלום ונהנים מקנייה קבוצתית.",
    link: "/קטגוריות",
    linkText: "התחל עגלה משותפת",
  },
  {
    icon: Store,
    label: "פתח חנות",
    emoji: "🏪",
    color: "from-rose-500/25 to-rose-500/5",
    border: "border-rose-500/40",
    glowHsl: "347 77% 50%",
    description: "יש לך מוצרים למכירה? פתח חנות משלך ב-NiceGift, נהל מוצרים, מלאי ומכירות. אנחנו מטפלים בכל השאר.",
    link: "/הצטרף-כספק",
    linkText: "הצטרף כספק",
  },
];

const stats = [
  { icon: Sparkles, value: "3,000+", label: "מוצרים", desc: "מגוון ענק של מעל 3,000 מוצרים מכל הקטגוריות - גאדג'טים, מתנות, אלקטרוניקה ועוד. תמיד תמצאו משהו מיוחד." },
  { icon: TrendingUp, value: "א׳-ה׳", label: "שירות", desc: "צוות השירות זמין בימים א׳-ה׳ בין 9:00 ל-18:00. אפשר לשלוח פנייה בוואטסאפ או באימייל בכל שעה ונחזור אליכם." },
  { icon: Shield, value: "SSL", label: "מאובטח", desc: "התשלום מתבצע באמצעות מערכת סליקה מאובטחת. פרטי האשראי אינם נשמרים במערכות האתר." },
  { icon: Truck, value: "1-5 ימים", label: "משלוח", desc: "משלוח רגיל ₪25 לכל ספק תוך 1-5 ימי עסקים, מהיר ₪39 תוך 1-2 ימי עסקים. חינם כאשר סכום המוצרים מאותו ספק הוא ₪500 ומעלה." },
  { icon: RotateCcw, value: "30 יום", label: "החזרה", desc: "ניתן לבקש החזרה בתוך 30 יום מקבלת המוצר, בכפוף לתנאי מדיניות ההחזרות ולחריגים שבדין. עלות משלוח ההחזרה על הלקוח, למעט מוצר פגום." },
  { icon: Headphones, value: "תמיד", label: "כאן בשבילך", desc: "ליווי אישי לאורך כל תהליך הרכישה - מבחירת המוצר ועד קבלתו בדלת." },
];

const HomeDashboard = () => {
  const [active, setActive] = useState<number | null>(null);
  const [mobileFeature, setMobileFeature] = useState<number | null>(null);
  const [mobileStat, setMobileStat] = useState<number | null>(null);
  const isMobile = useIsMobile();

  return (
    <section className="container max-w-5xl mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-bl from-card via-card to-primary/[0.06]"
        style={{
          boxShadow: "0 0 40px hsl(var(--primary) / 0.08), 0 0 80px hsl(var(--primary) / 0.04), inset 0 1px 0 hsl(var(--primary) / 0.1)",
        }}
      >
        {/* Animated Neon Glows */}
        <motion.div
          className="absolute -top-24 -left-24 w-56 h-56 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.2), transparent 70%)" }}
          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-20 -right-20 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(var(--accent) / 0.15), transparent 70%)" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.06), transparent 60%)" }}
          animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        {/* Hex dot pattern - desktop only */}
        {!isMobile && (
          <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
            <svg width="100%" height="100%">
              <pattern id="dash-hex" x="0" y="0" width="50" height="58" patternUnits="userSpaceOnUse">
                <circle cx="25" cy="15" r="1.5" fill="hsl(var(--primary))" />
                <circle cx="0" cy="43" r="1.5" fill="hsl(var(--accent))" />
                <circle cx="50" cy="43" r="1.5" fill="hsl(var(--primary))" />
              </pattern>
              <rect width="100%" height="100%" fill="url(#dash-hex)" />
            </svg>
          </div>
        )}

        {/* Scan line animation - desktop only */}
        {!isMobile && (
          <motion.div
            className="absolute inset-x-0 h-px pointer-events-none top-0 z-[1]"
            style={{
              background: "linear-gradient(90deg, transparent 10%, hsl(var(--primary) / 0.4) 40%, hsl(var(--primary) / 0.6) 50%, hsl(var(--primary) / 0.4) 60%, transparent 90%)",
            }}
            animate={{ y: ["0%", "8000%"] }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          />
        )}

        <div className="relative p-5 md:p-8 z-[2]">
          {/* Header with glow */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <motion.h2
                className="text-base md:text-lg font-black text-foreground flex items-center gap-2"
                style={{ textShadow: "0 0 20px hsl(var(--primary) / 0.15)" }}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                👋 ברוכים הבאים ל-NiceGift
              </motion.h2>
              <p className="text-xs text-muted-foreground mt-0.5">קונים, משתפים, מרוויחים</p>
            </div>
            <motion.span
              className="text-[10px] bg-primary/15 text-primary font-bold px-2.5 py-1 rounded-full backdrop-blur-sm border border-primary/20"
              style={{ boxShadow: "0 0 12px hsl(var(--primary) / 0.2), inset 0 0 8px hsl(var(--primary) / 0.1)" }}
              animate={{ boxShadow: ["0 0 12px hsl(var(--primary) / 0.2)", "0 0 20px hsl(var(--primary) / 0.35)", "0 0 12px hsl(var(--primary) / 0.2)"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              💰 100 נק׳ = ₪1
            </motion.span>
          </div>

          {/* Feature cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {features.map((f, i) => {
              const Icon = f.icon;
              const isActive = active === i;
              return (
                <motion.div
                  key={f.label}
                  layout
                  onClick={() => setMobileFeature(i)}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.4 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className={`relative cursor-pointer rounded-2xl border transition-all duration-300 p-4 bg-gradient-to-br backdrop-blur-sm ${f.color} ${isActive ? f.border + " shadow-lg" : "border-border/30 hover:border-primary/25"}`}
                  style={isActive ? {
                    boxShadow: `0 0 25px hsl(${f.glowHsl} / 0.2), 0 8px 30px hsl(${f.glowHsl} / 0.12), inset 0 1px 0 hsl(${f.glowHsl} / 0.15)`,
                  } : {}}
                >
                  {/* Card neon edge on hover */}
                  <div
                    className="absolute bottom-0 inset-x-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-2xl"
                    style={{ background: `linear-gradient(90deg, transparent, hsl(${f.glowHsl}), transparent)`, opacity: isActive ? 1 : 0 }}
                  />

                  <div className="flex items-center gap-3 mb-1">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg transition-all duration-300 ${isActive ? "bg-primary/15 shadow-[0_0_12px_hsl(var(--primary)/0.2)]" : "bg-background/60"}`}>
                      {f.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-bold text-foreground block">{f.label}</span>
                    </div>
                    <motion.div
                      animate={{ rotate: isActive ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronLeft size={14} className="text-muted-foreground" />
                    </motion.div>
                  </div>

                  <AnimatePresence>
                    {isActive && !isMobile && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <p className="text-xs text-muted-foreground leading-relaxed mt-3 mb-3">
                          {f.description}
                        </p>
                        <Link
                          to={f.link}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
                          style={{ textShadow: "0 0 8px hsl(var(--primary) / 0.3)" }}
                        >
                          {f.linkText}
                          <ChevronLeft size={12} />
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Stats bar with glassmorphism */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-6 pt-5 border-t border-primary/10">
            {stats.map((s, i) => {
              const Icon = s.icon;
              const statButton = (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + i * 0.05 }}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl bg-background/40 backdrop-blur-sm border border-border/20 hover:border-primary/20 hover:bg-primary/5 transition-all duration-300 group/stat cursor-pointer"
                  style={{ boxShadow: "inset 0 1px 0 hsl(var(--primary) / 0.05)" }}
                  onClick={() => setMobileStat(i)}
                >
                  <Icon size={14} className="text-primary group-hover/stat:drop-shadow-[0_0_6px_hsl(var(--primary)/0.5)] transition-all duration-300" />
                  <p className="text-sm font-black text-primary leading-none"
                    style={{ textShadow: "0 0 10px hsl(var(--primary) / 0.2)" }}
                  >
                    {s.value}
                  </p>
                  <p className="text-[9px] text-muted-foreground">{s.label}</p>
                </motion.button>
              );

              return <div key={s.label}>{statButton}</div>;
            })}
          </div>

          {/* Active indicator with pulsing glow */}
          <div className="flex items-center justify-center mt-4">
            <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
              <motion.span
                className="inline-block w-2 h-2 rounded-full bg-green-500"
                animate={{ boxShadow: ["0 0 4px #22c55e", "0 0 12px #22c55e", "0 0 4px #22c55e"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              לחץ על כל כרטיס כדי לגלות עוד
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile Feature Popup */}
      {(
        <Dialog open={mobileFeature !== null} onOpenChange={() => setMobileFeature(null)}>
          <DialogContent className="max-w-[90vw] rounded-2xl p-0 overflow-hidden border-primary/30 bg-card">
            {mobileFeature !== null && (() => {
              const f = features[mobileFeature];
              const Icon = f.icon;
              return (
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br ${f.color}`}>
                      {f.emoji}
                    </div>
                    <DialogHeader className="flex-1 space-y-0">
                      <DialogTitle className="text-base font-black text-foreground">{f.label}</DialogTitle>
                    </DialogHeader>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {f.description}
                  </p>
                  <Link
                    to={f.link}
                    onClick={() => setMobileFeature(null)}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-primary bg-primary/10 px-4 py-2.5 rounded-xl hover:bg-primary/15 transition-colors w-full justify-center"
                  >
                    {f.linkText}
                    <ChevronLeft size={14} />
                  </Link>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Mobile Stat Popup */}
      {(
        <Dialog open={mobileStat !== null} onOpenChange={() => setMobileStat(null)}>
          <DialogContent className="max-w-[90vw] rounded-2xl p-0 overflow-hidden border-primary/30 bg-card">
            {mobileStat !== null && (() => {
              const s = stats[mobileStat];
              const Icon = s.icon;
              return (
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon size={20} className="text-primary" />
                    </div>
                    <DialogHeader className="flex-1 space-y-0">
                      <DialogTitle className="text-base font-black text-foreground">{s.value} — {s.label}</DialogTitle>
                    </DialogHeader>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};

export default HomeDashboard;
